import React, { useState, useMemo, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Spinner,
  Modal,
} from "react-bootstrap";
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaFileExcel,
  FaUpload,
  FaInfoCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import DepartmentTopbar from "../SuperAdmin/components/DepartmentTopbar";
import DepartmentSidebar from "../SuperAdmin/components/DepertmentSidebar";
import InstallmentModal from "./InstallmentModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import ImportBeneficiaryModal from "./ImportBeneficiaryModal";

function HfaList() {
  const navigate = useNavigate();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInstallmentModal, setShowInstallmentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkContent, setRemarkContent] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [installmentFilter, setInstallmentFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("token");

  const fetchBeneficiaries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/list-hfa-beneficiary.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success && Array.isArray(response.data.data)) {
        setBeneficiaries(response.data.data);
      } else {
        setBeneficiaries([]);
      }
    } catch (error) {
      toast.error("Failed to fetch beneficiaries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBeneficiaries();
  }, []);

  const handleShowInstallmentModal = (row) => {
    setSelectedBeneficiary(row);
    setShowInstallmentModal(true);
  };

  const handleCloseInstallmentModal = () => {
    setShowInstallmentModal(false);
    setSelectedBeneficiary(null);
  };

  const handleShowDeleteModal = (row) => {
    setSelectedBeneficiary(row);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedBeneficiary(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedBeneficiary) return;
    try {
      const response = await axios.post(
        `${API_URL}/beneficiary-delete.php`,
        { id: selectedBeneficiary.id },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // console.log(response);
      if (response.data.success) {
        toast.success("Beneficiary deleted successfully!");
        fetchBeneficiaries();
      } else {
        toast.error(response.data.message || "Failed to delete beneficiary.");
      }
    } catch (error) {
      toast.error("An error occurred while deleting.");
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleAddInstallment = () => {
    fetchBeneficiaries();

    handleCloseInstallmentModal();
  };

  const handleImportSuccess = () => {
    fetchBeneficiaries();
    setShowImportModal(false);
  };

  // Handlers for Remark Modal
  const handleShowRemarkModal = (remark) => {
    if (remark && remark.trim() !== "") {
      setRemarkContent(remark);
    } else {
      setRemarkContent("No Remark Available.");
    }
    setShowRemarkModal(true);
  };

  const handleCloseRemarkModal = () => {
    setShowRemarkModal(false);
    setRemarkContent("");
  };

  // Handlers for Edit Installment Modal
  const handleShowEditModal = (row, installmentNumber) => {
    const suffix =
      installmentNumber === 1
        ? "st"
        : installmentNumber === 2
        ? "nd"
        : installmentNumber === 3
        ? "rd"
        : "th";
    const amountKey = `${installmentNumber}${suffix}_installment_amount`;

    setEditingInstallment({
      beneficiary: row,
      installmentNumber: installmentNumber,
      suffix: suffix,
    });

    setEditAmount(row[amountKey] || "");

    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingInstallment(null);
    setEditAmount("");
  };

  const handleEditSubmit = async () => {
    if (!editingInstallment) return;

    try {
      const { beneficiary, installmentNumber, suffix } = editingInstallment;
      const fieldName = `${installmentNumber}${suffix}_installment_amount`;

      const response = await axios.post(
        `${API_URL}/reupdate-installment.php`,
        {
          id: beneficiary.id,
          [fieldName]: editAmount,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        //  console.log(response);
        toast.success("Installment amount updated successfully!");
        fetchBeneficiaries();
        handleCloseEditModal();
      } else {
        toast.error(
          response.data.message || "Failed to update installment amount."
        );
      }
    } catch (error) {
      toast.error("An error occurred while updating installment amount.");
    }
  };

  const filteredBeneficiaries = useMemo(() => {
    return beneficiaries.filter((b) => {
      const matchesSearch = searchTerm
        ? b.Beneficiary_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.Beneficiary_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.Beneficiary_fathername.toLowerCase().includes(
            searchTerm.toLowerCase()
          )
        : true;

      const [inst, status] = installmentFilter.split("_");
      const instNumber = parseInt(inst);
      let matchesInstallment = true;
      if (installmentFilter !== "all") {
        const suffix =
          instNumber === 1
            ? "st"
            : instNumber === 2
            ? "nd"
            : instNumber === 3
            ? "rd"
            : "th";
        const amountKey = `${instNumber}${suffix}_installment_amount`;
        const dateKey = `${instNumber}${suffix}_installment_date`;
        
        if (status === "paid") {
          // Consider an installment as paid if either amount or date exists
          matchesInstallment = !!(b[amountKey] || b[dateKey]);
        } else if (status === "unpaid") {
          // Consider an installment as unpaid if both amount and date don't exist
          matchesInstallment = !b[amountKey] && !b[dateKey];
        }
      }

      let matchesDate = true;
      if (startDate && endDate) {
        matchesDate = false;
        for (let i = 1; i <= 5; i++) {
          const suffix =
            i === 1 ? "st" : i === 2 ? "nd" : i === 3 ? "rd" : "th";
          const dateKey = `${i}${suffix}_installment_date`;
          const instDate = b[dateKey];
          if (instDate && instDate >= startDate && instDate <= endDate) {
            matchesDate = true;
            break;
          }
        }
      }

      return matchesSearch && matchesInstallment && matchesDate;
    });
  }, [beneficiaries, searchTerm, installmentFilter, startDate, endDate]);

  const handleExport = () => {
    const dataToExport = filteredBeneficiaries.map((b) => ({
      "Beneficiary Code": b.Beneficiary_code,
      Name: b.Beneficiary_name,
      "Father Name": b.Beneficiary_fathername,
      "Ward No": b.ward_no,
      "1st Inst Amount": b["1st_installment_amount"] || "",
      "1st Inst Date": b["1st_installment_date"] || "",
      "1st Inst Remark": b["1st_installment_remark"] || "",
      "2nd Inst Amount": b["2nd_installment_amount"] || "",
      "2nd Inst Date": b["2nd_installment_date"] || "",
      "2nd Inst Remark": b["2nd_installment_remark"] || "",
      "3rd Inst Amount": b["3rd_installment_amount"] || "",
      "3rd Inst Date": b["3rd_installment_date"] || "",
      "3rd Inst Remark": b["3rd_installment_remark"] || "",
      "4th Inst Amount": b["4th_installment_amount"] || "",
      "4th Inst Date": b["4th_installment_date"] || "",
      "4th Inst Remark": b["4th_installment_remark"] || "",
      "5th Inst Amount": b["5th_installment_amount"] || "",
      "5th Inst Date": b["5th_installment_date"] || "",
      "5th Inst Remark": b["5th_installment_remark"] || "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Beneficiaries");
    XLSX.writeFile(workbook, "HFA_Beneficiary_Report.xlsx");
  };

  const renderInstallmentCell = (row, installmentNumber) => {
    const suffix =
      installmentNumber === 1
        ? "st"
        : installmentNumber === 2
        ? "nd"
        : installmentNumber === 3
        ? "rd"
        : "th";
    const amountKey = `${installmentNumber}${suffix}_installment_amount`;
    const dateKey = `${installmentNumber}${suffix}_installment_date`;
    const remarkKey = `${installmentNumber}${suffix}_installment_remark`;

    const amount = row[amountKey];
    const date = row[dateKey];
    const remark = row[remarkKey];

    return (amount !== undefined && amount !== null) && date ? (
      <div className="d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="fw-bold">
            ₹{parseFloat(amount).toLocaleString("en-IN")}
          </div>
          <small className="text-muted">{date}</small>
        </div>
        <div className="ms-2 flex flex-col  gap-1">
          <Button
            variant="link"
            size="sm"
            className="p-0 text-info"
            onClick={() => handleShowRemarkModal(remark)}
            title="View Remark"
          >
            <FaInfoCircle />
          </Button>
          <Button
            variant="link"
            size="sm"
            className="p-0 text-warning"
            onClick={() => handleShowEditModal(row, installmentNumber)}
            title="Edit Installment"
          >
            <FaEdit />
          </Button>
        </div>
      </div>
    ) : (
      "N/A"
    );
  };

  const columns = [
    {
      name: "Beneficiary Code",
      selector: (row) => row.Beneficiary_code,
      sortable: true,
      grow: 2,
    },
    {
      name: "Name",
      selector: (row) => row.Beneficiary_name,
      sortable: true,
      grow: 2,
    },
    {
      name: "Father's Name",
      selector: (row) => row.Beneficiary_fathername,
      sortable: true,
      grow: 2,
    },
    {
      name: "Ward No",
      selector: (row) => row.ward_no,
      sortable: true,
      width: "100px",
    },
    {
      name: "1st Inst",
      cell: (row) => renderInstallmentCell(row, 1),
      center: true,
    },
    {
      name: "2nd Inst",
      cell: (row) => renderInstallmentCell(row, 2),
      center: true,
    },
    {
      name: "3rd Inst",
      cell: (row) => renderInstallmentCell(row, 3),
      center: true,
    },
    {
      name: "4th Inst",
      cell: (row) => renderInstallmentCell(row, 4),
      center: true,
    },
    {
      name: "5th Inst",
      cell: (row) => renderInstallmentCell(row, 5),
      center: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex flex-column flex-md-row gap-2">
          <Button
            variant="info"
            size="sm"
            onClick={() => handleShowInstallmentModal(row)}
          >
            Installment
          </Button>
          <Button
            variant="warning"
            size="sm"
            onClick={() =>
              navigate(`/departmentDashboard/edit-hfa-beneficiary/${row.id}`)
            }
          >
            <FaEdit />
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleShowDeleteModal(row)}
          >
            <FaTrash />
          </Button>
        </div>
      ),
      width: "220px",
    },
  ];

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex" style={{ minHeight: "calc(100vh - 56px)" }}>
        <DepartmentSidebar />
        <div
          className="flex-grow-1 p-2 p-md-3"
          style={{ background: "#f0f2f5", overflowX: "hidden" }}
        >
          <Card
            className="shadow-sm"
            style={{ border: "none", borderRadius: "18px" }}
          >
            <Card.Header
              as="h5"
              style={{
                background: "#fff",
                borderBottom: "1px solid #eee",
                padding: "15px 20px",
              }}
            >
              HFA Department - Beneficiary List
            </Card.Header>
            <Card.Body className="p-2 p-md-3">
              <Form className="p-2 p-md-3 border-bottom">
                <Row className="g-2 g-md-3 align-items-end">
                  <Col xs={12} sm={6} md={4}>
                    <Form.Label>Search Beneficiary</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="search"
                        placeholder="Search by name, code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col xs={12} sm={6} md={4}>
                    <Form.Label>Filter by Installment</Form.Label>
                    <Form.Select
                      value={installmentFilter}
                      onChange={(e) => setInstallmentFilter(e.target.value)}
                    >
                      <option value="all">All Statuses</option>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <option key={`paid-${i}`} value={`${i}_paid`}>
                          {i}
                          {i === 1
                            ? "st"
                            : i === 2
                            ? "nd"
                            : i === 3
                            ? "rd"
                            : "th"}{" "}
                          Installment Paid
                        </option>
                      ))}
                      {[1, 2, 3, 4, 5].map((i) => (
                        <option key={`unpaid-${i}`} value={`${i}_unpaid`}>
                          {i}
                          {i === 1
                            ? "st"
                            : i === 2
                            ? "nd"
                            : i === 3
                            ? "rd"
                            : "th"}{" "}
                          Installment Unpaid
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={6} sm={6} md={2}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col xs={6} sm={6} md={2}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                </Row>
                <Row className="mt-2 mt-md-3">
                  <Col className="d-flex flex-wrap gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      className="mb-1"
                      onClick={() =>
                        navigate("/departmentDashboard/add-hfa-beneficiary")
                      }
                    >
                      <FaPlus /> Add New
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      className="mb-1"
                      onClick={handleExport}
                    >
                      <FaFileExcel /> Export
                    </Button>
                    <Button
                      variant="info"
                      size="sm"
                      className="mb-1"
                      onClick={() => setShowImportModal(true)}
                    >
                      <FaUpload /> Import
                    </Button>
                  </Col>
                </Row>
              </Form>
              <div
                className="table-responsive"
                style={{
                  overflowX: "auto",
                  maxWidth: "100%",
                }}
              >
                <DataTable
                  columns={columns}
                  data={filteredBeneficiaries}
                  pagination
                  highlightOnHover
                  responsive
                  progressPending={loading}
                  progressComponent={
                    <div className="p-5 text-center">
                      <Spinner animation="border" />
                    </div>
                  }
                  customStyles={{
                    table: {
                      style: {
                        minWidth: "1000px",
                      },
                    },
                    headRow: {
                      style: {
                        backgroundColor: "#f8f9fa",
                        borderBottom: "2px solid #dee2e6",
                      },
                    },
                    rows: {
                      style: {
                        minHeight: "50px",
                      },
                    },
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      {selectedBeneficiary && (
        <InstallmentModal
          show={showInstallmentModal}
          handleClose={handleCloseInstallmentModal}
          beneficiary={selectedBeneficiary}
          onSuccess={handleAddInstallment}
        />
      )}
      {selectedBeneficiary && (
        <DeleteConfirmationModal
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          beneficiaryName={selectedBeneficiary.Beneficiary_name}
          onConfirm={handleConfirmDelete}
        />
      )}
      <ImportBeneficiaryModal
        show={showImportModal}
        handleClose={() => setShowImportModal(false)}
        onSuccess={handleImportSuccess}
      />

      {/* Remark Modal */}
      <Modal show={showRemarkModal} onHide={handleCloseRemarkModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Installment Remark</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="p-3">
            <p className="mb-0">{remarkContent}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRemarkModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Installment Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Edit {editingInstallment?.installmentNumber}
            {editingInstallment?.installmentNumber === 1
              ? "st"
              : editingInstallment?.installmentNumber === 2
              ? "nd"
              : editingInstallment?.installmentNumber === 3
              ? "rd"
              : "th"}{" "}
            Installment Amount
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>ID</Form.Label>
              <Form.Control
                type="number"
                readOnly
                value={editingInstallment?.beneficiary?.Beneficiary_code}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={editingInstallment?.beneficiary?.Beneficiary_name}
                readOnly
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount (₹)</Form.Label>
              <Form.Control
                type="number"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            Update Amount
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default HfaList;
